import { List, ListItemAction, ListItemProperty } from "@/features/list";
import { trpc } from "@/hooks/trpc";
import {
  BatchClearanceDefinition,
  BatchClearanceDefinitionId,
} from "@brewbase/db/types";
import { createFileRoute, Link } from "@tanstack/react-router";
import NewBatchClearanceDefinitionDialog from "@/features/new-batch-clearance-definition-dialog";
import { Button } from "@/components/ui/button";
import { Trash2Icon } from "lucide-react";
import { toast } from "sonner";
import { RequireCapability } from "@/components/require-capability";

export const Route = createFileRoute("/settings/batch-clearance/")({
  component: () => (
    <RequireCapability cap="batch-clearance:configure">
      <RouteComponent />
    </RequireCapability>
  ),
});

function RouteComponent() {
  const utils = trpc.useUtils();
  const {
    data: definitions,
    isLoading: isLoadingDefinitions,
    error,
  } = trpc.batches.batchClearance.listDefs.useQuery();

  const deleteDefinitionMutation =
    trpc.batches.batchClearance.deleteDef.useMutation({
      onSuccess: () => {
        toast.success(`Definition  deleted successfully.`);
        utils.batches.batchClearance.listDefs.invalidate();
        utils.batches.batchClearance.getDefWithDetailsById.invalidate();
      },
      onError: (err, variables) => {
        toast.error(`Failed to delete definition: ${err.message}`);
      },
    });

  const handleDelete = (id: BatchClearanceDefinition["id"], name: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete the definition "${name}"? This action cannot be undone.`,
      )
    ) {
      deleteDefinitionMutation.mutate(id);
    }
  };

  if (isLoadingDefinitions) {
    return (
      <div className="container mx-auto px-4 py-6">Loading definitions...</div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6 text-red-500">
        Error loading definitions: {error.message}
      </div>
    );
  }

  if (!definitions) {
    return (
      <div className="container mx-auto px-4 py-6">No definitions found.</div>
    );
  }

  const listColumns: ListItemProperty<BatchClearanceDefinition, any>[] = [
    {
      key: "name",
      label: "Name",
      fieldType: "string",
    },
    {
      key: "description",
      label: "Description",
      fieldType: "string",
    },
    {
      key: "updated_at",
      label: "Last Updated",
      fieldType: "date",
    },
    // {
    //   key: "actions" as any, // Cast to any to allow custom key not in BatchClearanceDefinition
    //   label: "Actions",
    //   fieldType: "string",
    //   render: (item) => (
    //     <Button
    //       variant="ghost"
    //       size="icon"
    //       onClick={() => handleDelete(item.id, item.name)}
    //       disabled={
    //         deleteDefinitionMutation.isPending &&
    //         deleteDefinitionMutation.variables === item.id
    //       }
    //       className="text-destructive hover:text-destructive-foreground hover:bg-destructive size-8"
    //       aria-label="Delete definition"
    //     >
    //       <Trash2Icon className="size-4" />
    //     </Button>
    //   ),
    // },
  ];

  const actions: ListItemAction<BatchClearanceDefinition>[] = [
    {
      key: "delete",
      label: "Delete",
      position: "right",
      render: (item) => (
        <Button
          variant="destructiveGhost"
          size="icon"
          onClick={() => handleDelete(item.properties.id, item.properties.name)}
        >
          <Trash2Icon className="size-4" />
        </Button>
      ),
    },
  ];

  return (
    <div className="container mx-auto px-4 py-6 flex flex-col grow min-h-0 gap-4">
      <div>
        <h1 className="text-lg font-semibold">Batch Clearance</h1>
        <p className="text-sm text-muted-foreground">
          Manage batch clearance definitions and criteria.
        </p>
      </div>
      <List
        data={definitions.map((def) => ({
          id: def.id.toString(),
          href: `/settings/batch-clearance/${def.id}`,
          properties: def,
        }))}
        properties={listColumns}
        actions={actions}
        customToolbar={<NewBatchClearanceDefinitionDialog />}
      />
    </div>
  );
}
